import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import UndoIcon from "@mui/icons-material/Undo";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutlined";
import Box from "@mui/material/Box";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import BookmarksOutlinedIcon from "@mui/icons-material/BookmarksOutlined";
import Button from "@mui/material/Button";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import { type RowAction } from "_shared/_views/tables/row-actions";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Loader from "_shared/_views/loading/loader";
import EntityMember from "_shared/entity-members/entity-member";
import TeamRosterTable, {
  TEAM_MEMBER_ROLE,
  TEAM_ROSTER_PERSON_COLUMNS,
  teamRosterCell,
  type TeamRosterAddAction,
  type TeamRosterRow,
} from "_shared/entity-members/_views/team-roster-table";
import RowLink from "_shared/_views/tables/row-link";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { isKnownToLackCredentials } from "marketplace/individuals/profiles/professional-credentials";
import MarketplaceTeam from "marketplace/teams/team";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import {
  proposalTeamMemberItemId,
  useRemovedProposalTeamMembers,
  type ProposalTeamRemovedMember,
} from "work/proposals/negotiation/proposal-team-removed-members";
import ProposalFieldTimelineDropdown, {
  type ProposalFieldTimelineRevertEntry,
} from "work/proposals/negotiation/proposal-field-timeline-dropdown";
import {
  proposalCollectionItemRevertValue,
  proposalScalarFieldRevertValue,
} from "work/proposals/negotiation/proposal-collection-item-revert";
import {
  fieldHasPendingChange,
  pendingChangeTableRowStyles,
} from "work/proposals/comments/_view/pending-change-field-highlight";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import {
  teamLeaderRoleCell,
  type TeamLeaderRoleAction,
} from "work/proposals/leader-handover/_views/team-leader-role-cell";
import ProposalDeleteActionButton from "work/proposals/_views/_shared/proposal-delete-action-button";
import FieldUndoButton, { FIELD_UNDO_LABEL } from "work/proposals/_views/_buttons/field-undo-button";
import useAddAllColleagues, {
  ADD_ALL_COLLEAGUES_LABEL,
} from "work/work-agreements/teams/vendor-team/_views/members/add-all-colleagues-action";
import {
  mayAddAllColleagues,
  rosterLegalEntityId,
} from "work/work-agreements/teams/vendor-team/_views/members/add-all-colleagues";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import TeamLeaderSelector from "work/work-agreements/teams/vendor-team/_views/leader/team-leader-selector";
import VendorTeamSelection from "work/work-agreements/teams/vendor-team/_views/marketplace/marketplace-vendor-team-selector";
import {
  TeamLeaderRoleSelect,
  teamMemberRoleSelectCell,
} from "work/work-agreements/teams/vendor-team/_views/members/team-member-role-select";
import TeamPeoplePicker from "work/work-agreements/teams/people-picker/_views/team-people-picker";
import type {
  TeamPeopleChange,
  TeamPeoplePickerMode,
} from "work/work-agreements/teams/people-picker/team-people-selection";
import { TeamPeopleSourceId } from "work/work-agreements/teams/people-picker/team-people-sources";
import useSessionUser from "permissions/session/use-session-user";
import { shellTopBannersHeight } from "_shared/_views/shell-top-banners";

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(1),
}));
const TeamControls = styled("div")(({ theme }) => ({
  alignItems: "stretch",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  width: "100%",
}));
const TeamControlsGroup = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));
const TeamContainer = styled(Box)(({ theme }) => ({
  columnGap: theme.spacing(10),
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  paddingBottom: theme.spacing(3),
  rowGap: theme.spacing(3),
  width: "100%",
  [theme.breakpoints.down("lg")]: {
    columnGap: theme.spacing(3),
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    paddingBottom: 0,
  },
}));
/**
 * Says why the leader controls are absent for an actor who may still edit the roster. Without it the
 * two halves of the team look arbitrarily inconsistent — members editable, leader not — and a
 * reviewer is left to conclude the page is broken (#4854).
 */
/** What the people picker says beside the Leader role for an actor who may not change who leads. */
const TEAM_IDENTITY_LOCKED_LEADER_REASON = "Only the proposal creator can change the team leader";
const TEAM_IDENTITY_LOCK_MESSAGE =
  "Only the proposal creator can change the team leader or choose a different team, and only " +
  "before the proposal is first submitted. You can still change who is on the team.";
const TeamIdentityLockNotice = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontStyle: "italic",
  paddingTop: theme.spacing(1),
}));
/**
 * What a member's row says when the roster holds them but the directory could not name them. Dropping
 * the row instead — which this view did until #4955 — told the actor the team was one person smaller
 * than it is, and a person nobody can see is a person nobody can remove. Naming them by their raw
 * userId is not the alternative (#4830): a guid identifies nobody to a reader.
 */
const UNNAMED_MEMBER = "Unnamed member";
/**
 * Room for one of a roster row's editing controls — Make Leader, Change Leader, remove — which are
 * dense icon buttons of 32px at most.
 */
const ROSTER_EDIT_CONTROL_WIDTH = 32;
/** Room for a row's field history, a medium icon button carrying its change badge. */
const ROSTER_HISTORY_CONTROL_WIDTH = 40;
/** The cell's own padding either side of its controls, at MRT's comfortable density. */
const ROSTER_ACTIONS_CELL_PADDING = 32;

/**
 * The controls column, as wide as the busiest row's controls and no wider.
 *
 * A draft roster row carries up to three — Make Leader, remove, and the field's history — which come
 * to 136px, inside the ~140px this surface has beside `TEAM_ROSTER_PERSON_COLUMNS_WIDTH` at its
 * narrowest. A team template hides the history, so its rows carry one control each, and the 140px
 * the column used to ask for everywhere left a lone trash icon at the far end of an empty strip.
 */
function rosterActionsColumnWidthFor(controls: {
  leaderEditControls: number;
  memberEditControls: number;
  historyIsShown: boolean;
}): number {
  const editControls = Math.max(controls.leaderEditControls, controls.memberEditControls);
  const historyWidth = controls.historyIsShown ? ROSTER_HISTORY_CONTROL_WIDTH : 0;
  const controlsWidth = Math.max(ROSTER_EDIT_CONTROL_WIDTH, editControls * ROSTER_EDIT_CONTROL_WIDTH + historyWidth);
  return controlsWidth + ROSTER_ACTIONS_CELL_PADDING;
}
/**
 * What the member row's one-click promotion is called. Exported so the browser harness and the tab's
 * own specs name it exactly once.
 */
export const PROMOTE_MEMBER_TO_LEADER_LABEL = "Make Leader";
/** What the leader row's demotion is called, in its confirmation and on a phone's row menu (#6861). */
export const DEMOTE_LEADER_TO_MEMBER_LABEL = "Make Member";
/**
 * Why a vendor's leader cannot be demoted. A vendor's draft is opened through the seat its creator
 * holds, so the API refuses to save it with that seat empty (#5658), and the tab seats the vendor
 * again in an empty slot. Demoting would only bring them straight back.
 */
export const VENDOR_LEADER_DEMOTION_UNAVAILABLE_REASON =
  "A vendor's draft keeps its leader. Use Change Leader to choose a different one.";
/**
 * Why a leader without credentials cannot be demoted. A leader need not list them, a member must
 * (#5800), so the API refuses the roster that makes them a member.
 */
export const LEADER_WITHOUT_CREDENTIALS_DEMOTION_REASON =
  "Can't be a member until their profile lists professional credentials";
/** Which roster this is, stamped on its section — what the browser tests and the leader popover find it by. */
const ROSTER_SURFACE = "draft-vendor-team";
/**
 * What the roster's add control calls "take this team's people from a marketplace team".
 *
 * Exported so the browser harness names it exactly once: it used to be a button above the roster and
 * is now an entry behind the add control's dropdown (#4955).
 */
export const FROM_MARKETPLACE_TEAM_LABEL = "From Marketplace Team";
/**
 * The people picker's ways in (#6602): the add control's own press, and the shortcuts behind its
 * dropdown that open the same picker on a mode or a source. Exported so the specs and the browser
 * harness name each once.
 */
export const ADD_PEOPLE_LABEL = "Add People";
export const ADD_LEADER_LABEL = "Add Leader";
export const FROM_BOOKMARKS_LABEL = "From Bookmarks";
/** What the picker's drawer is titled, by what it was opened to choose. */
const PEOPLE_PICKER_TITLES: Record<TeamPeoplePickerMode, string> = { people: "Add People", leader: "Add Leader" };
const UnnamedMemberName = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontStyle: "italic",
}));
const TeamLoader = styled(Loader)(() => ({
  justifyContent: "flex-start",
}));
const selectionPanelWidth = "min(500px, 100vw)";
/** The people picker shows its sources beside its results, so its drawer is wider than a single list. */
const peoplePickerPanelWidth = "min(760px, 100vw)";
const Drawer = styled(MuiDrawer)(({ theme }) => ({
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    boxSizing: "border-box",
    height: `calc(100% - 64px - ${shellTopBannersHeight()})`,
    maxWidth: "100vw",
    overflow: "hidden",
    top: `calc(64px + ${shellTopBannersHeight()})`,
    width: selectionPanelWidth,
    // The app bar is 64px from `sm` up and 56px below it (`headers/header.tsx`).
    [theme.breakpoints.down("sm")]: {
      height: `calc(100% - ${theme.spacing(7)} - ${shellTopBannersHeight()})`,
      top: `calc(${theme.spacing(7)} + ${shellTopBannersHeight()})`,
    },
  },
}));
const SelectionPanel = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition" && prop !== "wide",
})<{ open?: boolean; wide?: boolean }>(({ theme, open, wide }) => ({
  "& .MuiDrawer-paper": {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
      duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
    }),
    width: open ? (wide ? peoplePickerPanelWidth : selectionPanelWidth) : 0,
  },
}));
const SelectionPanelContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "wide",
})<{ wide?: boolean }>(({ wide }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
  maxWidth: "100vw",
  minHeight: 0,
  width: wide ? peoplePickerPanelWidth : selectionPanelWidth,
}));
const SelectionPanelTitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: theme.spacing(2, 2, 1),
}));
const SelectionPanelContent = styled("div")(() => ({
  display: "flex",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
}));
const SelectionPanelActions = styled("div")(({ theme }) => ({
  backgroundColor: "#FAFAFA",
  padding: theme.spacing(1.5, 2, 10),
}));
const SelectionPanelCancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

type SelectionPanelType = "leader" | "member" | "marketplaceTeam" | "people";

/** What the people picker was opened to do: choose people or a leader, starting from which source. */
type PeoplePickerRequest = { mode: TeamPeoplePickerMode; initialSource?: TeamPeopleSourceId };

/** A roster member as the team holds them, beside the person the directory could make of them. */
type RosterMember = {
  member: EntityMember;
  individual?: Individual;
};

/**
 * A vendor-team member's canonical collection identity is the compound `userId:legalEntityId` (see
 * proposal-operation-identity.ts), not just the userId `ProposalField.TeamMember` carries, so the
 * dropdown is told that identity explicitly via `collectionItemId`.
 */
function memberCollectionItemId(member: Individual | EntityMember | undefined): string | undefined {
  if (!member?.userId || !member.legalEntityId) return undefined;
  return `${member.userId.value}:${member.legalEntityId.value}`;
}

/**
 * Everything the roster's Name column should match a person on. A search key only: the column is
 * ORDERED by the row's `sortText`, so that two members whose names tie are not separated by the
 * address appended here (#5164).
 */
function rosterSearchText(individual: Individual | undefined, fallback: string): string {
  if (!individual) return fallback;
  const email = individual.email?.value ?? "";
  return [individual.fullName, email].filter((word) => word.length > 0).join(" ");
}

type EditVendorTeamProps = {
  className?: string;
  /**
   * Save as Template, from the proposal tab that holds this roster. Its own button beside the add
   * control, not an entry in its dropdown, so a reader sees it without opening a menu (#5808).
   */
  saveAsTemplateAction?: TeamRosterAddAction;
  /** Actions in the leader row's Role cell, such as handing a draft over to the leader (#6579). */
  leaderActions?: readonly TeamLeaderRoleAction[];
  leader: EntityMember | null;
  members: EntityMember[];
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  searchParams?: URLSearchParams;
  /** Hides the per-field change-history controls beside the leader, each member, and the team header. */
  hideFieldHistory?: boolean;
  /**
   * Whether this actor may change WHICH TEAM this is — its leader and its marketplace identity — as
   * opposed to who is on it. The two are different permissions on a proposal: the roster is
   * negotiable content that anyone with proposal edit rights may change, while the team's identity
   * is initial proposal setup, reserved to the creator until the proposal is first submitted
   * (`canConfigureInitialProposalSetup`).
   *
   * When false the leader controls and "From Marketplace Team" are withdrawn while the member
   * controls stay live, because offering them produced edits the save path then discarded in
   * silence (#4854). Defaults to true, so callers with no such distinction — the team-template
   * editor, where the team IS the thing being authored — are unaffected.
   */
  canChangeTeamIdentity?: boolean;
  pendingChangeFields?: ProposalField[];
  /**
   * Whether this team is restricted. Not a rule about who may join — it decides what the roster
   * costs, because a restricted team's every member must approve the proposal before it can be
   * hired. "Add all colleagues" says so before it adds anyone.
   */
  teamRestricted?: boolean;
  onLeaderUpdated?: (leader: EntityMember | null) => void;
  /**
   * Called with the member the actor asked to lead the team, from the control on that member's own row
   * (#5668). One click and no confirm step: the caller seats them and carries the person who was
   * leading back onto the member list.
   *
   * Absent, no such control is drawn. That is what keeps the promotion to the surfaces where the
   * leader is still the proposal's to choose — a draft, saved or not. A submitted proposal draws its
   * team through `TeamRedlineSelection` instead, where the leader is fixed as the negotiator.
   */
  onMemberPromotedToLeader?: (member: EntityMember) => void;
  /**
   * Called when the actor makes the leader a member, from the leader row's Role select (#6861). The
   * roster asks first; the caller moves the leader onto the member list and leaves the slot empty.
   *
   * Absent, the leader's role is plain text. Only a draft passes it: from submission on, a team must
   * have a leader, and the leader changes through the handover instead.
   */
  onLeaderDemoted?: () => void;
  /**
   * Called with several members to add in one change, because a caller accumulates additions onto a
   * roster captured at render: told one at a time, it would keep only the last. Without it, "add all colleagues" is offered only where a caller can take a batch.
   */
  onMembersAdded?: (members: EntityMember[]) => void;
  /**
   * Called with the member to take off the roster.
   *
   * The id is still optional, and every caller still reads a missing one as "clear the roster" — but
   * nothing HERE calls it that way any more. The whole-roster trash beside the heading was one of the
   * two icon-only buttons #4955's review asked to be taken off the team tables, and removal is now
   * only ever decided on a member's own row. The callers keep their clear-all branch because it is
   * their own contract rather than this view's, and narrowing it reaches three surfaces this change
   * has no business in.
   */
  onMemberRemoved?: (userId?: Guid) => void;
  /**
   * Called with a team member reverted to a prior value (parsed from that value's history entry). The
   * caller applies it through the same builder setter as any other member edit, so the revert flows
   * through the same `queueProposalUpdate` path and lands as a `CollectionItemUpdated`/
   * `CollectionItemAdded` op — never a raw `FieldSet`.
   */
  onMemberReverted?: (member: EntityMember) => void;
  /**
   * Called with the leader reverted to a prior value (parsed from that value's history entry). The
   * caller applies it through the leader's own setter (`updateTeamOnProposalBuilder`, the same one
   * `onLeaderUpdated` uses), so the revert flows through `queueProposalUpdate` as a plain
   * `/vendorTeam/leader` `FieldSet` — never a raw whole-team overwrite touching `members`.
   *
   * `/vendorTeam/leader` is a scalar field (see proposal-command-builder.ts), not a collection item, so
   * this is routed through `ProposalFieldTimelineDropdown`'s `scalarPath`/structured-scalar mode rather
   * than its collection-item mechanism: deriving an `itemId` from `ProposalField.TeamLeader(id)`'s id
   * would match no event (scalar `FieldSet` events never carry an `itemId`) and render a permanently
   * empty menu.
   */
  onLeaderReverted?: (leader: EntityMember) => void;
  onMarketplaceTeamSelected?: (team: MarketplaceTeam) => void;
  /**
   * Called with everything the people picker chose, in one change: the people to put on the team and,
   * if one was chosen, its new leader (#6602). One call rather than a leader update and a member
   * update, because a caller builds each update on the roster it rendered, and the second would undo
   * the first. The caller seats the leader and keeps whoever led before on the team as a member.
   *
   * The roster's add control and its shortcuts all open the people picker, so this is how the roster
   * gains anybody but a whole marketplace team or the firm's every colleague.
   */
  onPeopleAdded: (change: TeamPeopleChange) => void;
  negotiationId?: string;
  viewerScope?: string;
  topControls?: React.ReactNode;
  /**
   * Extra actions rendered in the same button row as "From Marketplace Team", so a caller's own
   * action (e.g. the proposal team tab's "Create Template") lines up with it instead of sitting in a
   * separate row above the team controls. Use `topControls` for non-button content.
   */
  toolbarActions?: React.ReactNode;
  /**
   * The caller's own ways onward from this roster, after the ways of filling it in the add control's
   * dropdown, such as the proposal team tab's "Ask Several Firms" (#6638).
   */
  extraAddActions?: readonly TeamRosterAddAction[];
  /** What the roster says while it has nobody. "No Team Members" without it. */
  emptyMessage?: React.ReactNode;
  selectionPresentation?: "dialog" | "drawer";
  onSelectionDrawerOpenChange?: (isOpen: boolean) => void;
  /**
   * A saved draft's members removed since its last save, and their Undo (#6995). Each stays drawn,
   * struck through, with Undo in place of Remove until the save. Absent everywhere else.
   */
  draftRemovals?: VendorTeamDraftRemovals;
};

export type VendorTeamDraftRemovals = {
  members: readonly EntityMember[];
  onMemberUndone: (member: EntityMember) => void;
};

/** No draft removals: one array, so the memos below do not change on every render. */
const NO_DRAFT_REMOVED_MEMBERS: readonly EntityMember[] = [];

/**
 * The vendor team's draft roster, drawn as ONE table — Name, Email, Company, Role — through the
 * shared `TeamRosterTable`, the way the Fee Schedule tab is one table (#4955).
 *
 * The leader and a member still differ in every way they did: the leader is a scalar proposal field
 * (`/vendorTeam/leader`) and a member is a collection item keyed by the compound
 * `userId:legalEntityId`, and they are governed by *different permissions* — see
 * `canChangeTeamIdentity`, where the roster belongs to anyone holding `canEditDraft` while the team's
 * identity is the creator's pre-submission setup (#4854). That difference is stated by the Role column
 * and enforced by the row's own controls, which is where it belongs; a second table said it by
 * geometry and cost the reader any view of the team as a whole.
 *
 * Used outside proposals too, by the team-template editor, where the team is the thing being authored
 * and every one of those distinctions is simply absent.
 */
export default function EditVendorTeam(props: Readonly<EditVendorTeamProps>) {
  const {
    className,
    leader,
    members,
    negotiationId,
    viewerScope,
    disableEditing,
    fieldLockTooltip,
    searchParams,
    hideFieldHistory,
    topControls,
    toolbarActions,
    extraAddActions,
    pendingChangeFields,
    teamRestricted,
    onLeaderUpdated,
    onMemberPromotedToLeader,
    onLeaderDemoted,
    onMembersAdded,
    onMemberRemoved,
    onMemberReverted,
    onLeaderReverted,
    onMarketplaceTeamSelected,
    onPeopleAdded,
    onSelectionDrawerOpenChange,
    saveAsTemplateAction,
    draftRemovals,
  } = props;
  const draftRemovedMembers = draftRemovals?.members ?? NO_DRAFT_REMOVED_MEMBERS;
  const selectionPresentation = props.selectionPresentation ?? "dialog";
  // Read off `props` rather than the destructuring above so the default reads as a default rather
  // than as a destructuring pattern the React Compiler cannot lower (see app/CLAUDE.md).
  const teamIdentityIsLocked = props.canChangeTeamIdentity === false;

  const memberUserIds: string[] = useMemo(() => {
    return members.map((member) => member.userId.value);
  }, [members]);

  // #region Removed members
  // The members the team no longer has, read from the team's own history and kept as struck-through,
  // read-only rows so each keeps the history of the member they were (#4954) — the whole-team history
  // control that used to be the only place they survived is gone. Nothing that edits the roster is
  // offered on them.
  //
  // A saved draft's own removals since its last save are drawn the same way, from `draftRemovals`, and
  // offer Undo (#6995). Each is still on the saved team, so it counts as present here, and cannot also
  // come back from the history as a second row.
  const showFieldHistory = Boolean(!hideFieldHistory && negotiationId && viewerScope);
  const presentMemberItemIds = useMemo(
    () => [...members, ...draftRemovedMembers].flatMap((member) => {
      const itemId = proposalTeamMemberItemId(member);
      return itemId ? [itemId] : [];
    }),
    [draftRemovedMembers, members],
  );
  const removedMembers = useRemovedProposalTeamMembers({
    negotiationId: showFieldHistory ? negotiationId : undefined,
    viewerScope: showFieldHistory ? viewerScope : undefined,
    presentItemIds: presentMemberItemIds,
  });
  // #endregion

  const individualsToFetch = useMemo(() => {
    const ids = new Set<string>();
    if (leader) ids.add(leader.userId.value);
    memberUserIds.forEach((userId) => ids.add(userId));
    removedMembers.forEach((removed) => ids.add(removed.member.userId.value));
    draftRemovedMembers.forEach((member) => ids.add(member.userId.value));
    return ids;
  }, [draftRemovedMembers, leader, memberUserIds, removedMembers]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    individualsToFetch.size ? { userIds: [...individualsToFetch] } : skipToken,
  );

  const individuals: Individual[] = useMemo(() => {
    return Individual.fromJSONArray(individualQuery.data) ?? [];
  }, [individualQuery.data]);

  const individualMap: Map<string, Individual> = useMemo(() => {
    const map = new Map<string, Individual>();
    individuals.forEach((individual) => {
      if (individual.userId?.value) {
        map.set(individual.userId.value, individual);
      }
    });
    return map;
  }, [individuals]);

  const leaderIndividual: Individual | undefined = useMemo(() => {
    if (!leader) return undefined;
    return individualMap.get(leader.userId.value);
  }, [leader, individualMap]);

  /**
   * The roster as the team holds it, each member beside whatever the directory could make of them.
   *
   * Built from `members`, never from the directory's answer: pairing the two and keeping the pair is
   * what stops a member the lookup cannot resolve from vanishing off the roster altogether.
   */
  const memberRoster: RosterMember[] = useMemo(() => {
    return members.map((member) => ({ member, individual: individualMap.get(member.userId.value) }));
  }, [members, individualMap]);

  const userPermissions = useSelector(getSessionPermissions);

  const [teamLeaderSelectorAnchor, setTeamLeaderSelectorAnchor] = React.useState<HTMLElement>();
  const [selectionPanel, setSelectionPanel] = React.useState<SelectionPanelType>();
  const [selectionPanelFilterViewOpen, setSelectionPanelFilterViewOpen] = React.useState(false);
  const [peoplePickerRequest, setPeoplePickerRequest] = React.useState<PeoplePickerRequest>({ mode: "people" });
  // A lock column, not a lock avatar: the avatar is the person's own mark, and swapping it out told
  // the reader who was locked at the cost of no longer telling them who it was.
  const showLockColumn = Boolean(disableEditing && fieldLockTooltip);
  // Who gets the member row's one-click promotion: an actor who may edit this team AND may change who
  // leads it. The caller decides the surface by handing the action in at all — see
  // `onMemberPromotedToLeader` (#5668).
  const showPromoteToLeader = Boolean(onMemberPromotedToLeader) && !disableEditing && !teamIdentityIsLocked;
  // The leader's Role select, the other way round: the same actor, and a caller that offers it.
  const showDemoteLeader = Boolean(onLeaderDemoted) && !disableEditing && !teamIdentityIsLocked;
  // Whether a row's history control draws anything. Both history controls render nothing until the
  // negotiation stream exists — a draft before its first save — and a phone sizes the controls
  // column from what the rows hand it, so an empty one would still claim a 40px slot (#5907).
  const historyIsShown = !hideFieldHistory && Boolean(negotiationId) && Boolean(viewerScope);
  const rosterActionsColumnWidth = rosterActionsColumnWidthFor({
    leaderEditControls: !disableEditing && !teamIdentityIsLocked ? 1 : 0,
    memberEditControls: disableEditing ? 0 : 1,
    historyIsShown,
  });

  const confirm = useConfirmDialog();
  const { openDialog, popDialog } = useDialog();
  const session = useSession();
  const sessionUser = useSessionUser();
  const viewingAsVendor = Boolean(session.context?.viewingAsVendor);

  const closeSelectionPanel = useCallback(() => {
    setSelectionPanel(undefined);
    setSelectionPanelFilterViewOpen(false);
    onSelectionDrawerOpenChange?.(false);
  }, [onSelectionDrawerOpenChange]);

  const openSelectionPanel = useCallback(
    (panel: SelectionPanelType) => {
      setSelectionPanel(panel);
      setSelectionPanelFilterViewOpen(false);
      onSelectionDrawerOpenChange?.(true);
    },
    [onSelectionDrawerOpenChange],
  );

  React.useEffect(() => {
    return () => onSelectionDrawerOpenChange?.(false);
  }, [onSelectionDrawerOpenChange]);

  const handleCoworkerSelectedAsLeader = useCallback(
    async (leader?: Individual) => {
      if (!leader) {
        onLeaderUpdated?.(null);
        return;
      }
      onLeaderUpdated?.(leader?.toEntityMember());
    },
    [onLeaderUpdated],
  );

  /**
   * Handles what happens when a member is removed from the vendor-team
   *
   */
  const handleLeaderRemoved = useCallback(async () => {
    const response = await confirm({
      title: "Remove Leader?",
      message: `Do you want to remove the leader from the team?`,
      okButtonText: "Remove",
    });

    if (response === ConfirmResponse.Cancel) return;

    onLeaderUpdated?.(null);
  }, [confirm, onLeaderUpdated]);

  /**
   * Makes a member the leader from their Role cell (#6602). Asked first, unlike the one-click icon it
   * replaces (#5668): the select sits among the roster's text, where a stray choice is easy, and the
   * half of the change the actor did not name — whoever led becomes a member — is said before it
   * happens rather than after.
   */
  async function promoteFromRoleCell(member: EntityMember, name: string) {
    const response = await confirm({
      title: `Make ${name} the leader?`,
      message: leaderIndividual
        ? `${leaderIndividual.fullName} becomes a member and stays on the team. A team has one leader.`
        : "A team has one leader.",
      okButtonText: PROMOTE_MEMBER_TO_LEADER_LABEL,
    });
    if (response === ConfirmResponse.Cancel) return;
    onMemberPromotedToLeader?.(member);
  }

  /**
   * Why the leader may not be made a member, or undefined when they may (#6861). A vendor's draft keeps
   * its leader; a leader without credentials could not join as a member (#5800).
   */
  function leaderDemotionUnavailableReason(): string | undefined {
    if (viewingAsVendor) return VENDOR_LEADER_DEMOTION_UNAVAILABLE_REASON;
    if (isKnownToLackCredentials(leaderIndividual)) return LEADER_WITHOUT_CREDENTIALS_DEMOTION_REASON;
    return undefined;
  }

  /**
   * Makes the leader a member from their Role cell (#6861). Asked first, as the promotion is, and the
   * prompt says what the actor did not choose: the team has no leader until somebody is chosen.
   */
  async function demoteFromRoleCell(name: string) {
    const response = await confirm({
      title: `Make ${name} a member?`,
      message: `${name} stays on the team. The team has no leader until you choose one, and it needs one before the proposal is submitted.`,
      okButtonText: DEMOTE_LEADER_TO_MEMBER_LABEL,
    });
    if (response === ConfirmResponse.Cancel) return;
    onLeaderDemoted?.();
  }

  /**
   * Handles what happens when a member is removed from the vendor-team
   *
   * @param userId The userId of the member to remove
   */
  const handleMemberRemoved = useCallback(
    async (userId?: Guid) => {
      if (!userId) return;

      const response = await confirm({
        title: "Remove Member?",
        message: `Do you want to remove the member from the team?`,
        okButtonText: "Remove",
      });

      if (response === ConfirmResponse.Cancel) return;

      onMemberRemoved?.(userId);
    },
    [confirm, onMemberRemoved],
  );

  /**
   * Opens the user profile dialog for the given individual
   *
   * @param individualId The id of the individual to open the dialog for
   */
  const openUserProfileDialog = useCallback(
    (individualId: Guid) => {
      openDialog({
        component: <ViewIndividualProfile individualId={individualId} />,
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: true,
        },
      });
    },
    [openDialog],
  );

  const handleMemberRevert = useCallback(
    (entry: ProposalFieldTimelineRevertEntry) => {
      // This dropdown instance is always in collection-item mode (a team member), so `entry.operation`
      // is always CollectionItemAdded/Updated at runtime — narrowed here to satisfy
      // `proposalCollectionItemRevertValue`'s parameter type, which excludes `FieldSet`.
      if (entry.operation?.kind !== "CollectionItemAdded" && entry.operation?.kind !== "CollectionItemUpdated") return;
      const revertValue = proposalCollectionItemRevertValue(entry.operation);
      if (revertValue === undefined) return;
      const revertedMember = EntityMember.fromJSON(revertValue as never);
      if (!revertedMember) return;
      onMemberReverted?.(revertedMember);
    },
    [onMemberReverted],
  );

  // `entry.operation` is a `FieldSet` built by `buildProposalScalarFieldRevertOperation` (structured
  // scalar mode — see proposal-field-timeline-dropdown.tsx's `scalarPath`); only its `value` is used,
  // the same way `handleMemberRevert` only uses its collection operation's `value`.
  const handleLeaderRevert = useCallback(
    (entry: ProposalFieldTimelineRevertEntry) => {
      // This dropdown instance is always in structured-scalar mode (the team leader), so
      // `entry.operation` is always FieldSet at runtime — narrowed here to satisfy
      // `proposalScalarFieldRevertValue`'s parameter type, which excludes the collection-item kinds.
      if (entry.operation?.kind !== "FieldSet") return;
      const revertValue = proposalScalarFieldRevertValue(entry.operation);
      if (revertValue === undefined) return;
      const revertedLeader = EntityMember.fromJSON(revertValue as never);
      if (!revertedLeader) return;
      onLeaderReverted?.(revertedLeader);
    },
    [onLeaderReverted],
  );

  function leaderRosterRow(leader: Individual): TeamRosterRow {
    const field = ProposalField.TeamLeader(leader.userId);
    const hasPendingChange = fieldHasPendingChange(pendingChangeFields, field);
    const demotionUnavailableReason = showDemoteLeader ? leaderDemotionUnavailableReason() : undefined;
    const roleSelect = showDemoteLeader ? (
      <TeamLeaderRoleSelect
        name={leader.fullName}
        memberUnavailableReason={demotionUnavailableReason}
        onMakeMember={() => void demoteFromRoleCell(leader.fullName)}
      />
    ) : undefined;

    return {
      id: "vendor-team-leader",
      searchText: rosterSearchText(leader, ""),
      sortText: leader.fullName,
      rowSx: hasPendingChange ? (theme) => pendingChangeTableRowStyles(theme) : undefined,
      avatar: <IndividualAvatar individual={leader} />,
      // `RowLink`, the one way a name cell says it opens something — see #5244. The hand-rolled
      // link it replaces was an anchor with no `href`, which Enter and Space do not activate.
      name: (
        <RowLink ariaLabel={leader.fullName} onClick={() => openUserProfileDialog(leader.id)}>
          {leader.fullName}
        </RowLink>
      ),
      email: teamRosterCell(leader.email?.value),
      company: teamRosterCell(leader.entityName),
      role: teamLeaderRoleCell(props.leaderActions ?? [], roleSelect),
      actions: (
        <>
          {/* A vendor who can sign always has a leader on their draft — the tab seats them in an empty
              slot — so removing one only brings them straight back. What a vendor can do is put a
              different coworker in the slot, from the popover the old leader field used to open. A
              vendor who cannot sign is not seated (#5658), and fills the empty slot the same way. */}
          {!disableEditing && !teamIdentityIsLocked && viewingAsVendor && (
            <Tooltip title="Change Leader">
              <IconButton
                aria-label="Change Leader"
                size="small"
                onClick={(event) => setTeamLeaderSelectorAnchor(event.currentTarget)}
              >
                <EditOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
          {!disableEditing && !teamIdentityIsLocked && !viewingAsVendor && (
            <Tooltip title="Remove Leader">
              <span>
                <ProposalDeleteActionButton aria-label="Remove Leader" onClick={handleLeaderRemoved} />
              </span>
            </Tooltip>
          )}
          {!hideFieldHistory && (
            <>
              {/* Reverting the leader IS changing the leader, so an actor who may not change the
                  team's identity gets the read-only history instead of the revert menu. */}
              {disableEditing || teamIdentityIsLocked ? (
                <ProposalFieldHistoryButton field={field} negotiationId={negotiationId} viewerScope={viewerScope} />
              ) : (
                <ProposalFieldTimelineDropdown
                  field={ProposalField.TeamLeader()}
                  scalarPath="/vendorTeam/leader"
                  negotiationId={negotiationId}
                  viewerScope={viewerScope}
                  onRevert={handleLeaderRevert}
                />
              )}
            </>
          )}
        </>
      ),
      // The same controls as data, for a phone — see `TeamRosterRow.menuActions`.
      menuActions: [
        // The Role select's demotion, as a phone's row menu entry behind the same confirmation, and
        // disabled with the same reason where the select's Member option is.
        ...(showDemoteLeader
          ? [
            {
              key: "demote",
              label: DEMOTE_LEADER_TO_MEMBER_LABEL,
              icon: <PersonOutlineIcon />,
              disabled: Boolean(demotionUnavailableReason),
              disabledReason: demotionUnavailableReason,
              onClick: () => void demoteFromRoleCell(leader.fullName),
            },
          ]
          : []),
        ...(!disableEditing && !teamIdentityIsLocked && viewingAsVendor
          ? [
            {
              key: "change-leader",
              label: "Change Leader",
              icon: <EditOutlinedIcon />,
              onClick: (anchor: HTMLElement) => setTeamLeaderSelectorAnchor(anchor),
            },
          ]
          : []),
        ...(!disableEditing && !teamIdentityIsLocked && !viewingAsVendor
          ? [
            {
              key: "remove-leader",
              label: "Remove Leader",
              icon: <DeleteOutlinedIcon />,
              destructive: true,
              onClick: () => void handleLeaderRemoved(),
            },
          ]
          : []),
      ],
      trailingAction: !historyIsShown ? undefined : disableEditing || teamIdentityIsLocked ? (
        <ProposalFieldHistoryButton field={field} negotiationId={negotiationId} viewerScope={viewerScope} />
      ) : (
        <ProposalFieldTimelineDropdown
          field={ProposalField.TeamLeader()}
          scalarPath="/vendorTeam/leader"
          negotiationId={negotiationId}
          viewerScope={viewerScope}
          onRevert={handleLeaderRevert}
        />
      ),
    };
  }

  /**
   * A member the team no longer has: struck through, with nothing to remove or revert, and the history
   * of the member they were — scoped to them — as the row's one control (#4954).
   */
  function removedMemberRosterRow(removed: ProposalTeamRemovedMember): TeamRosterRow {
    const individual = individualMap.get(removed.member.userId.value);
    const displayName = individual?.fullName ?? (individualQuery.isLoading ? "Loading..." : "Former member");
    const removedHistory = (
      <ProposalFieldHistoryButton
        field={ProposalField.TeamMember(removed.member.userId)}
        collectionItemId={removed.itemId}
        negotiationId={negotiationId}
        viewerScope={viewerScope}
      />
    );
    return {
      id: `removed-${removed.itemId}`,
      searchText: rosterSearchText(individual, displayName),
      sortText: displayName,
      rowAttributes: { "data-removed-member": removed.itemId },
      // The whole row reads as a deletion, every person column and the avatar, as a removed document's
      // does (#6995).
      isRemoved: true,
      avatar: <IndividualAvatar individual={individual} />,
      name: <Typography>{displayName}</Typography>,
      email: teamRosterCell(individual?.email?.value),
      company: teamRosterCell(individual?.entityName),
      role: { content: TEAM_MEMBER_ROLE },
      actions: removedHistory,
      menuActions: [],
      trailingAction: historyIsShown ? removedHistory : undefined,
    };
  }

  /**
   * A member the draft took off the team since its last save: struck through, with Undo in place of
   * Remove, and the history of the member they were (#6995).
   */
  function draftRemovedMemberRosterRow(member: EntityMember, onMemberUndone: (member: EntityMember) => void): TeamRosterRow {
    const individual = individualMap.get(member.userId.value);
    const displayName = individual?.fullName ?? (individualQuery.isLoading ? "Loading..." : UNNAMED_MEMBER);
    const field = ProposalField.TeamMember(member.userId);
    const collectionItemId = memberCollectionItemId(member);
    const history = historyIsShown ? (
      <ProposalFieldHistoryButton
        field={field}
        collectionItemId={collectionItemId}
        negotiationId={negotiationId}
        viewerScope={viewerScope}
      />
    ) : undefined;
    return {
      id: `draft-removed-${collectionItemId ?? member.userId.value}`,
      searchText: rosterSearchText(individual, displayName),
      sortText: displayName,
      rowAttributes: { "data-draft-removed-member": collectionItemId ?? member.userId.value },
      // The whole row reads as a deletion, every person column and the avatar, as a removed document's
      // does (#6995).
      isRemoved: true,
      avatar: <IndividualAvatar individual={individual} />,
      name: <Typography>{displayName}</Typography>,
      email: teamRosterCell(individual?.email?.value),
      company: teamRosterCell(individual?.entityName),
      role: { content: TEAM_MEMBER_ROLE },
      actions: (
        <>
          <FieldUndoButton onUndo={() => onMemberUndone(member)} />
          {history}
        </>
      ),
      menuActions: [
        {
          key: "undo",
          label: FIELD_UNDO_LABEL,
          icon: <UndoIcon />,
          onClick: () => onMemberUndone(member),
        },
      ],
      trailingAction: history,
    };
  }

  /**
   * One member of the roster.
   *
   * A member the directory could not name keeps their row and every control on it — the actor has to be
   * able to see them and to take them off the team. Only the name and the profile link, which need an
   * individual, stand down.
   */
  function memberRosterRow(rosterMember: RosterMember): TeamRosterRow {
    const member = rosterMember.member;
    const individual = rosterMember.individual;
    const field = ProposalField.TeamMember(member.userId);
    const hasPendingChange = fieldHasPendingChange(pendingChangeFields, field);
    // The member's own history, not the whole roster's: a member's `ProposalField` carries their
    // user id, but their timeline path is the team's (#4954).
    const collectionItemId = memberCollectionItemId(individual ?? member);
    const memberName = individual?.fullName ?? UNNAMED_MEMBER;
    const memberMenuActions: RowAction[] = [];
    // A phone's row menu keeps the promotion as an entry of its own, behind the same confirmation as
    // the Role select it stands in for.
    if (showPromoteToLeader) {
      memberMenuActions.push({
        key: "promote",
        label: PROMOTE_MEMBER_TO_LEADER_LABEL,
        icon: <MilitaryTechOutlinedIcon />,
        onClick: () => void promoteFromRoleCell(member, memberName),
      });
    }
    if (!disableEditing) {
      memberMenuActions.push({
        key: "remove",
        label: "Remove Member",
        icon: <DeleteOutlinedIcon />,
        destructive: true,
        onClick: () => handleMemberRemoved(member.userId),
      });
    }

    return {
      id: collectionItemId ?? member.userId.value,
      searchText: rosterSearchText(individual, UNNAMED_MEMBER),
      sortText: individual?.fullName ?? UNNAMED_MEMBER,
      rowSx: hasPendingChange ? (theme) => pendingChangeTableRowStyles(theme) : undefined,
      avatar: <IndividualAvatar individual={individual} />,
      // A contact the API sent for someone whose marketplace profile is not published is who they
      // are with no profile behind it, so it is shown rather than offered as a link to a dialog
      // with nothing more to say (#5538).
      name: individual ? (
        individual.contactOnly ? (
          <Typography>{individual.fullName}</Typography>
        ) : (
          <RowLink ariaLabel={individual.fullName} onClick={() => openUserProfileDialog(individual.id)}>
            {individual.fullName}
          </RowLink>
        )
      ) : (
        <UnnamedMemberName>{UNNAMED_MEMBER}</UnnamedMemberName>
      ),
      // A member the directory could not name has no email and no firm to show either, and their
      // membership's GUIDs are not a stand-in for one — the columns say so with the roster's own mark.
      email: teamRosterCell(individual?.email?.value),
      company: teamRosterCell(individual?.entityName),
      // The Role cell is where the leader is changed (#6602), replacing the row's "Make Leader" icon
      // (#5668). Offered only to an actor who may change who leads: promoting somebody CHANGES the
      // leader, so one who may not change the team's identity reads the plain role instead.
      role: showPromoteToLeader
        ? teamMemberRoleSelectCell({ name: memberName, onMakeLeader: () => void promoteFromRoleCell(member, memberName) })
        : { content: TEAM_MEMBER_ROLE },
      actions: (
        <>
          {!disableEditing && (
            <Tooltip title="Remove Member">
              <span>
                <ProposalDeleteActionButton
                  aria-label="Remove Member"
                  onClick={() => handleMemberRemoved(member.userId)}
                />
              </span>
            </Tooltip>
          )}
          {!hideFieldHistory && (
            <>
              {disableEditing ? (
                <ProposalFieldHistoryButton
                  field={field}
                  collectionItemId={collectionItemId}
                  negotiationId={negotiationId}
                  viewerScope={viewerScope}
                />
              ) : (
                <ProposalFieldTimelineDropdown
                  field={field}
                  negotiationId={negotiationId}
                  viewerScope={viewerScope}
                  collectionItemId={collectionItemId}
                  onRevert={handleMemberRevert}
                />
              )}
            </>
          )}
        </>
      ),
      // The same controls as data, for a phone — see `TeamRosterRow.menuActions`.
      menuActions: memberMenuActions,
      trailingAction: !historyIsShown ? undefined : disableEditing ? (
        <ProposalFieldHistoryButton
          field={field}
          collectionItemId={collectionItemId}
          negotiationId={negotiationId}
          viewerScope={viewerScope}
        />
      ) : (
        <ProposalFieldTimelineDropdown
          field={field}
          negotiationId={negotiationId}
          viewerScope={viewerScope}
          collectionItemId={collectionItemId}
          onRevert={handleMemberRevert}
        />
      ),
    };
  }

  const handleMarketplaceTeamSelected = useCallback(
    (team: MarketplaceTeam) => {
      if (!team.leader.profile) throw new Error("Team leader profile is undefined");

      onMarketplaceTeamSelected?.(team);
      if (selectionPresentation === "drawer") {
        closeSelectionPanel();
      } else {
        popDialog();
      }
    },
    [closeSelectionPanel, onMarketplaceTeamSelected, popDialog, selectionPresentation],
  );

  const beginMarketplaceTeamSelection = useCallback(() => {
    if (selectionPresentation === "drawer") {
      openSelectionPanel("marketplaceTeam");
      return;
    }

    openDialog({
      title: "Select Marketplace Team",
      component: <VendorTeamSelection onNewSelection={handleMarketplaceTeamSelected} closeDialog={popDialog} />,
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }, [handleMarketplaceTeamSelected, openDialog, openSelectionPanel, popDialog, selectionPresentation]);

  // The roster's own ids, not the directory lookup's: that can be pending or have failed, and a leader
  // missing from the list would be added as a member — which `VendorTeam.ValidateInputAsync` refuses
  // outright.
  const alreadyOnTeamUserIds = useMemo(
    () => [...members.map((member) => member.userId), ...(leader ? [leader.userId] : [])],
    [leader, members],
  );
  const addAllColleagues = useAddAllColleagues({
    alreadyOnTeamUserIds,
    teamRestricted,
    onColleaguesChosen: (colleagues) => onMembersAdded?.(colleagues),
  });

  function getSelectionPanelTitle(): string {
    switch (selectionPanel) {
      case "marketplaceTeam":
        return "Select Marketplace Team";
      case "people":
        return PEOPLE_PICKER_TITLES[peoplePickerRequest.mode];
      default:
        return "";
    }
  }

  function renderSelectionPanelContent() {
    if (selectionPanel === "people") {
      return renderPeoplePicker(peoplePickerRequest, closeSelectionPanel);
    }

    if (selectionPanel === "marketplaceTeam") {
      return (
        <VendorTeamSelection
          searchParams={searchParams}
          onFilterViewOpenChange={setSelectionPanelFilterViewOpen}
          onNewSelection={handleMarketplaceTeamSelected}
          closeDialog={closeSelectionPanel}
        />
      );
    }

    return null;
  }

  // Choosing a marketplace team replaces the team's identity, not just its roster, so it belongs
  // with the leader controls behind `canChangeTeamIdentity`.
  const showMarketplaceTeamButton = Boolean(
    userPermissions?.includesAccessToMarketplaceSearch &&
      !disableEditing &&
      !teamIdentityIsLocked &&
      session.accountType === AccountType.Client,
  );

  const leaderIsLoading = Boolean(individualQuery.isLoading && leader && !leaderIndividual);
  // The leader is a row of the one table whichever party is reading, so a vendor's own team tab lays
  // out exactly as the client's view of it does. Only where a new leader comes from differs: a vendor
  // picks one of its coworkers, a client searches the marketplace.
  const leaderRows = leaderIndividual ? [leaderRosterRow(leaderIndividual)] : [];

  const membersAreLoading =
    individualQuery.isLoading && members.length > 0 && memberRoster.every((entry) => !entry.individual);
  const rosterRows: TeamRosterRow[] = [
    ...leaderRows,
    ...(membersAreLoading ? [] : memberRoster.map(memberRosterRow)),
    ...(draftRemovals && !disableEditing
      ? draftRemovedMembers.map((member) => draftRemovedMemberRosterRow(member, draftRemovals.onMemberUndone))
      : []),
    ...removedMembers.map(removedMemberRosterRow),
  ];
  const rosterIsLoading = membersAreLoading || leaderIsLoading;
  const showAddMember = !disableEditing && !membersAreLoading;
  // Whose roster this is, read off who is on it. A client may edit the vendor team too — the roster
  // is negotiable content — so without this the action would offer to put the client's own staff on
  // the firm's team. `onMembersAdded` is the other half: a caller that can only take one member at a
  // time cannot apply a batch, so it is not offered one.
  const canSearchMarketplace = Boolean(sessionUser?.canSearchMarketplace());
  const canReadMarketplaceDirectory = Boolean(sessionUser?.canReadMarketplaceIndividual());
  // Whether the picker may make anybody the leader: the identity permission, and a caller that can
  // seat one (#4854).
  const mayChooseLeader = !disableEditing && !teamIdentityIsLocked && Boolean(onLeaderUpdated) && !leaderIsLoading;

  // The vendor leader's firm, whose people a client is offered: a client has no coworkers at the firm
  // it hires, and a vendor has them as Coworkers (#6860).
  const leaderFirm =
    viewingAsVendor || !leader ? undefined : { legalEntityId: leader.legalEntityId.value, name: leaderIndividual?.entityName };

  function renderPeoplePicker(request: PeoplePickerRequest, close: () => void) {
    return (
      <TeamPeoplePicker
        mode={request.mode}
        initialSource={request.initialSource}
        rosterLeaderUserId={leader?.userId.value}
        rosterLeaderName={leaderIndividual?.fullName}
        // A leader is exempt from the credentials a member needs (#5800), so one replaced here may not
        // be able to stay on as a member; the picker asks for their removal instead.
        rosterLeaderLacksCredentials={isKnownToLackCredentials(leaderIndividual)}
        rosterMemberUserIds={memberUserIds}
        leaderUnavailableReason={mayChooseLeader ? undefined : TEAM_IDENTITY_LOCKED_LEADER_REASON}
        // A vendor's leader is one of its own coworkers, as the leader popover has always held it to.
        leaderLegalEntityId={viewingAsVendor ? session.user?.legalEntityId?.value : undefined}
        // A client staffing the vendor team is offered nobody from its own firm (#6859).
        clientLegalEntityId={viewingAsVendor ? undefined : session.user?.legalEntityId?.value}
        leaderFirm={leaderFirm}
        canSearchMarketplace={canSearchMarketplace}
        canReadMarketplaceDirectory={canReadMarketplaceDirectory}
        onAdd={(change) => {
          close();
          onPeopleAdded(change);
        }}
        onCancel={close}
      />
    );
  }

  function openPeoplePicker(request: PeoplePickerRequest) {
    if (selectionPresentation === "drawer") {
      setPeoplePickerRequest(request);
      openSelectionPanel("people");
      return;
    }
    openDialog({
      title: PEOPLE_PICKER_TITLES[request.mode],
      component: renderPeoplePicker(request, popDialog),
      contentSxProps: { display: "flex", height: "70vh", p: 0 },
      MuiProps: { fullWidth: true, maxWidth: "md" },
    });
  }

  const showAddAllColleagues = Boolean(
    showAddMember && onMembersAdded && mayAddAllColleagues(session.user?.legalEntityId, rosterLegalEntityId(leader, members)),
  );
  const addActions: TeamRosterAddAction[] = [];
  // One way in, the people picker, and shortcuts into it behind the dropdown (#6602). Choosing a
  // leader is still the identity permission and adding people the roster's (#4854): the picker is
  // told which, and offers the Leader role only to an actor who may change it.
  if (showAddMember) addActions.push({ label: ADD_PEOPLE_LABEL, onClick: () => openPeoplePicker({ mode: "people" }) });
  if (mayChooseLeader) {
    addActions.push({
      label: ADD_LEADER_LABEL,
      icon: <MilitaryTechOutlinedIcon fontSize="small" />,
      onClick: () => openPeoplePicker({ mode: "leader" }),
    });
  }
  // The other ways this roster gains people, behind the add control's dropdown rather than as buttons
  // of their own (#4955): "Add All Colleagues" replaces the icon-only button that sat beside the
  // heading, and "From Marketplace Team" the outlined button that sat above it. Both are still a way
  // of filling the team, which is what the split button's dropdown collects.
  if (showAddAllColleagues) {
    addActions.push({
      label: ADD_ALL_COLLEAGUES_LABEL,
      icon: <GroupAddOutlinedIcon fontSize="small" />,
      loading: addAllColleagues.isWorking,
      onClick: addAllColleagues.addAllColleagues,
    });
  }
  if (showMarketplaceTeamButton) {
    addActions.push({
      label: FROM_MARKETPLACE_TEAM_LABEL,
      icon: <GroupsIcon fontSize="small" />,
      onClick: beginMarketplaceTeamSelection,
    });
  }
  // Bookmarks are people the viewer already saved: the bookmark endpoints ask for no marketplace
  // permission, so this shortcut is not held to Marketplace Search the way discovery is.
  if (showAddMember) {
    addActions.push({
      label: FROM_BOOKMARKS_LABEL,
      icon: <BookmarksOutlinedIcon fontSize="small" />,
      onClick: () => openPeoplePicker({ mode: "people", initialSource: TeamPeopleSourceId.BookmarkedTeams }),
    });
  }
  if (!disableEditing && extraAddActions) addActions.push(...extraAddActions);

  // Drawn above the roster, because it explains the absence of the leader controls whichever
  // shape the leader takes.
  const teamIdentityLockNotice = teamIdentityIsLocked && !disableEditing && (
    <TeamIdentityLockNotice>{TEAM_IDENTITY_LOCK_MESSAGE}</TeamIdentityLockNotice>
  );

  return (
    <MainContainer className={className}>
      <TeamControls>
        {topControls}
        {/* "From Marketplace Team" used to stand here as a button of its own. It is one of the ways
            this roster can be filled, so it is now an entry behind the roster's add control instead —
            see `addActions` above (#4955). */}
        {toolbarActions && <TeamControlsGroup>{toolbarActions}</TeamControlsGroup>}
      </TeamControls>
      <TeamContainer>
        <TeamRosterTable
          title="Team Roster"
          surface={ROSTER_SURFACE}
          columns={TEAM_ROSTER_PERSON_COLUMNS}
          notice={teamIdentityLockNotice}
          rows={rosterRows}
          lockIndicator={showLockColumn ? <ReviewerEditLockIcon title={fieldLockTooltip} /> : undefined}
          emptyMessage={rosterIsLoading ? <TeamLoader text="Loading..." /> : (props.emptyMessage ?? "No Team Members")}
          noMatchesMessage="No matching team members"
          // Sized to the controls this surface's rows actually carry. The default is sized for the
          // active proposal's five, and this tab shares its width with the Templates panel, so every
          // pixel the pinned controls column does not need is a pixel the Role column gets to show.
          actionsColumnWidth={rosterActionsColumnWidth}
          enableSearch
          addActions={addActions}
          standaloneAction={disableEditing ? undefined : saveAsTemplateAction}
        />
        {/* No whole-team history here: a member's history lives on their row, and a removed member
            keeps a row of their own (#4954). No whole-roster "Remove All Members" either (#4955): it
            was an icon-only trash beside the heading, which the owner's review asked to be taken off
            the team tables, and removing a member is still offered on that member's own row. */}
        {viewingAsVendor && (
          <TeamLeaderSelector
            popoverAnchor={teamLeaderSelectorAnchor}
            teamLeaderUserId={leaderIndividual?.userId}
            onTeamLeaderUpdated={handleCoworkerSelectedAsLeader}
            onPopoverClose={() => {
              setTeamLeaderSelectorAnchor(undefined);
            }}
          />
        )}
      </TeamContainer>
      {selectionPresentation === "drawer" && (
        <SelectionPanel
          open={Boolean(selectionPanel)}
          wide={selectionPanel === "people"}
          anchor="right"
          variant="persistent"
          closeAfterTransition={false}
        >
          <SelectionPanelContainer wide={selectionPanel === "people"}>
            {!selectionPanelFilterViewOpen && (
              <SelectionPanelTitleBar>
                <Typography variant="h5">{getSelectionPanelTitle()}</Typography>
                <Tooltip title="Minimize panel">
                  <IconButton size="medium" onClick={closeSelectionPanel}>
                    <KeyboardTabIcon fontSize="large" />
                  </IconButton>
                </Tooltip>
              </SelectionPanelTitleBar>
            )}
            <SelectionPanelContent>{renderSelectionPanelContent()}</SelectionPanelContent>
            {/* The people picker has a footer of its own, with its Add beside its Cancel. */}
            {!selectionPanelFilterViewOpen && selectionPanel !== "people" && (
              <SelectionPanelActions>
                <SelectionPanelCancelButton fullWidth variant="outlined" color="primary" onClick={closeSelectionPanel}>
                  Cancel
                </SelectionPanelCancelButton>
              </SelectionPanelActions>
            )}
          </SelectionPanelContainer>
        </SelectionPanel>
      )}
    </MainContainer>
  );
}
